import { useCallback, useEffect, useState } from "react";

/**
 * PO 2026-02 · Support Diagnostics V1 — session-scoped enable/disable
 * with 60-minute automatic expiration. Distinct key from the QA
 * `jc:levi:diag` sticky so contractor-support cannot re-arm QA and
 * vice versa. Uses sessionStorage so closing the browser tab
 * automatically clears the flag.
 */
const KEY = "jc:support:diag";
const AT_KEY = "jc:support:diag:enabled_at";
export const SUPPORT_DIAG_TTL_MS = 60 * 60 * 1000;

function _readEnabled() {
  try {
    if (typeof window === "undefined") return false;
    const v = window.sessionStorage.getItem(KEY);
    if (v !== "1") return false;
    const at = Number(window.sessionStorage.getItem(AT_KEY) || 0);
    if (!at || Date.now() - at > SUPPORT_DIAG_TTL_MS) {
      window.sessionStorage.removeItem(KEY);
      window.sessionStorage.removeItem(AT_KEY);
      return false;
    }
    return true;
  } catch { return false; }
}

export function isSupportDiagnosticsEnabled() { return _readEnabled(); }

export function enableSupportDiagnostics() {
  try {
    window.sessionStorage.setItem(KEY, "1");
    window.sessionStorage.setItem(AT_KEY, String(Date.now()));
    window.dispatchEvent(new CustomEvent("jc:support:diag:changed"));
  } catch { /* noop */ }
}

export function disableSupportDiagnostics() {
  try {
    window.sessionStorage.removeItem(KEY);
    window.sessionStorage.removeItem(AT_KEY);
    window.dispatchEvent(new CustomEvent("jc:support:diag:changed"));
  } catch { /* noop */ }
}

export function useSupportDiagnostics() {
  const [enabled, setEnabled] = useState(() => _readEnabled());
  const refresh = useCallback(() => setEnabled(_readEnabled()), []);
  useEffect(() => {
    const on = () => refresh();
    window.addEventListener("jc:support:diag:changed", on);
    // Re-check every minute so the auto-expire visibly kicks in.
    const t = window.setInterval(refresh, 60 * 1000);
    return () => {
      window.removeEventListener("jc:support:diag:changed", on);
      window.clearInterval(t);
    };
  }, [refresh]);
  return { enabled, enable: enableSupportDiagnostics,
           disable: disableSupportDiagnostics, refresh };
}

export function collectClientSnapshot() {
  const w = typeof window !== "undefined" ? window : {};
  const nav = typeof navigator !== "undefined" ? navigator : {};
  const qa = (w.__leviQAState || {});
  const parity = (w.__leviParityDiag || {});
  return {
    captured_at: new Date().toISOString(),
    app: {
      route: (w.location && w.location.pathname) || null,
      href_origin: (w.location && w.location.origin) || null,
    },
    device: {
      user_agent: (nav.userAgent || "").slice(0, 240),
      is_mobile: /Mobi|Android|iPhone|iPad|iPod/i.test(nav.userAgent || ""),
      viewport: { w: w.innerWidth || null, h: w.innerHeight || null },
      dpr: w.devicePixelRatio || null,
      locale: nav.language || null,
    },
    levi: {
      voice_mode: qa.voice_mode || null,
      voice_id: qa.voice_id || null,
      stt_source: qa.stt_source || null,
      tts_source_last: w.__leviLastTtsSource || qa.tts_source || null,
      tts_fallback_fired: w.__leviLastTtsSource === "browser_tts_fallback",
      last_speech_turn: w.__leviLastSpeechTurn || null,
      levi_session_id: qa.levi_session_id || null,
    },
    session: {
      jc_session_id: qa.jc_session_id || null,
      levi_session_id: qa.levi_session_id || null,
    },
    network: {
      recent_calls: (qa.recent_calls || []).slice(-20),
    },
    parity_diag: parity,
    feature_flags: {
      REACT_APP_LEVI_VOICE_DIAG_ENABLED:
        String(process.env.REACT_APP_LEVI_VOICE_DIAG_ENABLED || ""),
    },
  };
}
